/**
 * Блок «Посев · сезон N» внутри попапа поля на /map.
 *
 * Запрос founder (staging, 2026-07-27): «нет ни одной кнопки, которая бы
 * перевела культуру на поле в состояние посеяно» + «добавил два вырезанных
 * поливных контура — как их засеять? непонятна механика». Оба ответа здесь:
 *
 *   · поле без зон  → строка «культура · статус» + кнопка «Отметить: посеяно»,
 *                     когда посев ещё в плане;
 *   · поле с зонами → та же строка ПО КАЖДОЙ зоне (круг / углы / чек) со
 *                     своей кнопкой — видно, что зона это самостоятельный
 *                     участок посева, а не декорация контура;
 *   · посева нет     → «Не запланировано» + ссылка «Запланировать →» в
 *                     карточку поля на вкладку «Посевы» (там полная форма).
 *
 * Данные и мутация — ТЕ ЖЕ хуки, что у карточки поля
 * (`useOccupancy` / `usePatchOccupancy` из components/fields/card/api.ts):
 * один контракт, одна инвалидация (`['field-card']`), никакого второго
 * источника правды. Статус переводится PATCH /occupancy/{id} {status:'sown'}.
 *
 * Рендерится порталом в слот `[data-testid="field-popover-sow-slot"]` внутри
 * HTML-попапа MapLibre (см. lib/map/fieldPopover.ts + pages/map/v2.tsx) —
 * поэтому компонент остаётся обычным React-узлом (react-query, контексты
 * сезона и прав работают), а карта/тайлы/фильтры не затрагиваются.
 *
 * Пока occupancy грузится (или если запрос упал) показываем строку из
 * MVT-свойств тайла (культура + фаза) — попап никогда не «пустеет».
 *
 * AK-509 (ADR-059 §3): статус — одно из 4 пользовательских значений
 * (Запланировано · Растёт · Убрано · Списано), словарь общий с
 * OccupancyProgress (`@/lib/sowings/stage`). Механика кнопки «Отметить:
 * посеяно» НЕ меняется — сев остаётся событием; после клика статус читается
 * как «Растёт», отдельного «Посеян» больше нет. Частичная уборка — цифра
 * «убрано X из Y га» рядом со статусом, не отдельное слово.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Loader2, Sprout } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CropChip } from '@/components/ui/crop-chip';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { useCan, PERM } from '@/lib/auth/usePermissions';
import { useActiveSeason, useSeasonGuard } from '@/lib/season/SeasonContext';
import { isSeasonCompleted } from '@/lib/season/computedStatus';
import { resolveSowingDisplayFor } from '@/lib/sowings/seasonDisplay';
import { phaseLabel } from '@/lib/map/fieldPopover';
import { useOccupancy, usePatchOccupancy } from '@/components/fields/card/api';
import { fieldStateLabel } from '@/components/fields/card/constants';
// ОДИН источник этапа и подписи на карту и на карточку поля (AK-434): раньше
// попап печатал «Не запланирован», а полоса прогресса на карточке — «План»
// для ОДНОГО и того же посева. AK-509: словарь свёрнут к 4 пользовательским
// значениям (ADR-059 §3) — частичная уборка/сев тут та же «Растёт» + цифра.
import {
  canMarkSown,
  sowingStage,
  sowingStageLabel,
  sowingStageQuantity,
} from '@/lib/sowings/stage';
import { sownUnitLeaves } from '@/components/fields/card/rotationLogic';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useMapStore } from '@/lib/map/useMapStoreV2';
import { isCropInLegend, useScopeCrops } from '@/lib/map/useScopeCrops';
import {
  useFieldStateLabel,
  type CodeLabeler,
} from '@/lib/reference/fieldCatalogs';
import type { OccupancyLeaf } from '@/components/fields/card/types';

export interface FieldSowBlockProps {
  fieldId: string;
  /** Номер/имя поля — в подтверждение «Посеяно: <культура> на <поле>». */
  fieldName: string;
  /** Культура из MVT-свойств тайла — мгновенный фолбэк, пока грузится occupancy. */
  fallbackCrop?: string | null;
  /** Фаза из MVT-свойств тайла (planned/growing/harvested/fallow). */
  fallbackPhase?: string | null;
  /**
   * Показать подтверждение успеха НА КАРТЕ (review head-web: общий
   * `lib/toast.ts` только пишет в консоль — `<Toaster>` в проекте не
   * смонтирован, founder ничего бы не увидел). `/map` уже умеет overlay-тост
   * (AddFieldPanel / ImportFilePanel / promotedToast), сюда его прокидывает
   * v2.tsx. Не передан — падаем на общий toast (тесты, любой не-карточный
   * вызов).
   */
  onSown?: (message: string) => void;
}

/** Ссылка «Запланировать →» — карточка поля, вкладка «Посевы» (см. tabs.ts). */
function planHref(fieldId: string): string {
  return `/fields/${fieldId}?tab=rotation`;
}

/** Что написано на строке занятия: культура, пар или «не запланировано». */
function leafSubject(
  leaf: OccupancyLeaf,
  fallowLabel: CodeLabeler,
): {
  kind: 'crop' | 'fallow' | 'none';
  label: string;
} {
  const s = leaf.sowing;
  if (!s) return { kind: 'none', label: 'Не запланировано' };
  if (s.occupancy_type === 'fallow') {
    // Пар/АМП — подпись из справочника состояний поля (АМП паром не называем).
    return { kind: 'fallow', label: fieldStateLabel(s.fallow_kind, fallowLabel) };
  }
  if (s.is_fallow) {
    // Пар, заведённый КУЛЬТУРОЙ каталога («Пар черный», «АМП мелиоративное
    // поле»). Имя занятия берём как есть — оно и есть подпись пара; лестницу
    // сева к нему не применяем.
    return { kind: 'fallow', label: s.crop_name ?? 'Пар' };
  }
  return { kind: 'crop', label: s.crop_name ?? 'Культура не указана' };
}

export function FieldSowBlock({
  fieldId,
  fieldName,
  fallbackCrop,
  fallbackPhase,
  onSown,
}: FieldSowBlockProps) {
  const { season, loading: seasonLoading } = useActiveSeason();
  const seasonId = season?.seasonId ?? null;
  // Сезон линзы уже прошёл — «Запланировано» на карте читается как
  // «Не реализован» (канон v3 §5). Подмена только показа, данные не трогаем.
  const seasonCompleted = season
    ? isSeasonCompleted({
        startsOn: season.startsOn,
        endsOn: season.endsOn,
        year: season.year,
        isCurrent: season.isCurrent,
      })
    : false;
  const occ = useOccupancy(fieldId, seasonId);
  const canWrite = useCan(PERM.cropWrite, 'u');
  const seasonGuard = useSeasonGuard();
  const patch = usePatchOccupancy(fieldId);
  // Вид пара — подпись из справочника (reference.field_states), не из константы.
  const fallowLabel = useFieldStateLabel();
  // UAT founder 10.08: попап пишет «Лён», а легенда красит поле «Не разобрано»
  // — человек не понимает, почему поле серое. Вердикт «признана справочником»
  // считает сервер и отдаёт в легенде: читаем ТОТ ЖЕ ответ (кэш общий с
  // палитрой), поэтому попап и легенда разойтись не могут. Имя культуры при
  // этом не прячем — прятать то, что человек сам завёл, хуже, чем объяснить.
  const activeScope = useMapStore((st) => st.activeScope);
  const legend = useScopeCrops(activeScope);

  /**
   * Пометку показываем ТОЛЬКО в текущем сезоне.
   *
   * Занятия попап берёт по сезону СЕССИИ (его переключают), а легенда строится
   * из `field_visual_attrs` — она per-active-season и про прошлый сезон ничего
   * не знает. Встав в прошлый сезон, мы бы обвинили в «не подтверждена» каждую
   * честно сматченную культуру того года: тот же дефект, что чинил этот PR,
   * только в обратную сторону (находка head-web).
   */
  function showUnconfirmed(cropId: string | null | undefined): boolean {
    if (!season?.isCurrent) return false;
    return isCropInLegend(legend.data, cropId) === false;
  }

  // Оптимистичный статус: строка перекрашивается сразу по клику, до ответа
  // сервера. Ошибка → откат (и тост с причиной от backend).
  const [optimistic, setOptimistic] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  // Снимаем локальный статус, когда сервер его догнал (инвалидация
  // ['field-card'] → refetch occupancy). Не снимаем сразу по ответу PATCH:
  // между ответом и приездом свежего occupancy строка мигнула бы обратно
  // в «План» вместе с кнопкой. Дальше правду ведёт сервер.
  useEffect(() => {
    const leaves = occ.data?.leaves ?? [];
    setOptimistic((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const leaf of leaves) {
        const s = leaf.sowing;
        if (s && next[s.sowing_id] === sowingStage(s)) {
          delete next[s.sowing_id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [occ.data]);

  async function markSown(leaf: OccupancyLeaf) {
    const s = leaf.sowing;
    if (!s) return;
    const id = s.sowing_id;
    setOptimistic((prev) => ({ ...prev, [id]: 'sown' }));
    setBusyId(id);
    try {
      await patch.mutateAsync({ sowingId: id, body: { status: 'sown' } });
      const target = leaf.subzone_id ? `${fieldName} · ${leaf.name}` : fieldName;
      const message = `Посеяно: ${s.crop_name ?? 'культура'} на ${target}`;
      if (onSown) onSown(message);
      else toast.success(message);
    } catch (err) {
      setOptimistic((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast.error(
        err instanceof ApiError ? err.detail : 'Не удалось отметить посев',
      );
    } finally {
      setBusyId(null);
    }
  }

  const title = season ? `Посев · сезон ${season.year}` : 'Посев';

  /**
   * Строка-фолбэк из MVT: культура и фаза, известные тайлу.
   *
   * Культуру печатаем ТОЛЬКО при «растущей» фазе. Пустая фаза и `fallow` у
   * статичного билдера означают пар (`phaseLabel(undefined) === 'Пар'`), а
   * связка «культура + Пар» — то самое P0-противоречие, которое сторожит
   * `sowingRowHtml` (у сидерального пара MV отдаёт и культуру, и fallow).
   * Здесь тот же инвариант: пар — самостоятельное утверждение без культуры.
   */
  const fallbackIsFallow = !fallbackPhase || fallbackPhase === 'fallow';
  const fallbackRow = (
    <div className="flex items-center gap-2 text-xs" data-testid="map-sow-fallback">
      {fallbackCrop && !fallbackIsFallow ? (
        <>
          <CropChip
            name={fallbackCrop}
            iconSize={14}
            className="min-w-0 text-foreground"
            fallbackIcon={<Sprout className="h-3.5 w-3.5 text-muted-foreground" />}
          />
          <span className="text-muted-foreground">
            · {phaseLabel(fallbackPhase ?? undefined)}
          </span>
        </>
      ) : (
        <span className="text-muted-foreground">
          {phaseLabel(fallbackPhase ?? undefined)}
        </span>
      )}
    </div>
  );

  let body: ReactNode;
  if (seasonLoading || (seasonId && occ.isLoading)) {
    body = (
      <div className="space-y-2">
        {fallbackRow}
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  } else if (!seasonId || occ.isError) {
    // Нет сезона в сессии или occupancy не отдалась — показываем то, что знает
    // тайл, и уводим в карточку, где есть полная форма посева.
    body = (
      <div className="space-y-2">
        {fallbackRow}
        <a href={planHref(fieldId)} className="text-xs font-medium text-primary hover:underline" data-testid="map-sow-plan-link">
          Открыть посевы →
        </a>
      </div>
    );
  } else {
    const all = occ.data?.leaves ?? [];
    // Только посевные единицы: у поля с зонами ответ содержит ещё и участок
    // «всё поле» (AK-449) — лишняя строка «не посеяно» поверх зон. Правило
    // одно на все экраны (sownUnitLeaves), чтобы карта, паспорт и итог по
    // полю не разъезжались.
    const leaves = sownUnitLeaves(all);
    // AK-485 (наряд map-popup-vs-label-bug Д3): `zoned` считался по СЫРОМУ
    // `all` (есть ли у поля зоны вообще), а строки рендерятся по `leaves`
    // (sownUnitLeaves отбрасывает зоны, если посев на весь контур) — попап
    // писал «Поле разбито на зоны» там, где ниже шла ОДНА строка «Всё поле».
    // Считаем zoned по тому же набору, что реально рендерится.
    const zoned = leaves.some((l) => l.subzone_id != null);
    body = (
      <div className="space-y-2">
        {zoned && (
          <div className="text-xs leading-tight text-muted-foreground">
            Поле разбито на зоны — каждая сеется отдельно.
          </div>
        )}
        {leaves.map((leaf) => {
          const s = leaf.sowing;
          const subject = leafSubject(leaf, fallowLabel);
          // Оптимистичный статус перекрывает УЖЕ РАЗРЕШЁННЫЙ (crop_status),
          // а не поле `stage`: sowingStage() читает `crop_status` первым, и
          // подмена `stage` не срабатывала бы вовсе — после тапа «Отметить:
          // посеяно» подпись и кнопка стояли бы старыми до refetch («толкай и
          // жди»). Тот же приём, что в OccupancyProgress.
          const pendingStage = s ? optimistic[s.sowing_id] : undefined;
          const eff = s
            ? pendingStage
              ? { ...s, crop_status: pendingStage, stage: pendingStage, status: pendingStage }
              : s
            : null;
          // Пар не сеют: кнопка «Отметить: посеяно» на паровом занятии —
          // предложение сделать бессмысленное действие.
          const showSow = canMarkSown(eff) && subject.kind === 'crop';
          const busy = Boolean(s) && busyId === s?.sowing_id;
          // Подпись статуса остаётся за `sowingStageLabel` — у него есть
          // честное «Без статуса» для кода вне словаря, а `getSowingStatusInfo`
          // на незнакомом коде возвращает сам код и протёк бы сырым на экран.
          // От правила завершённого сезона берём ТОЛЬКО подмену «Не реализован»
          // и её подсказку (вердикт head-web по sha 16363c51).
          const unrealized = resolveSowingDisplayFor(eff, seasonCompleted);
          const stageLabel = unrealized?.isUnrealized
            ? unrealized.label
            : sowingStageLabel(eff);
          return (
            <div
              key={leaf.subzone_id ?? 'whole'}
              className="space-y-1"
              data-testid="map-sow-row"
            >
              {zoned && (
                <div className="truncate text-xs font-medium text-foreground">
                  {leaf.name}
                </div>
              )}
              <div className="flex items-center justify-between gap-2 text-xs">
                {subject.kind === 'crop' ? (
                  <span className="flex min-w-0 items-center gap-2">
                    <CropChip
                      name={subject.label}
                      iconSize={14}
                      className="min-w-0 text-foreground"
                      fallbackIcon={
                        <Sprout className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      }
                    />
                    {/* Провайдер тултипа локальный: блок монтируется в попап
                        MapLibre, а не в общее дерево экрана — глобального
                        провайдера над ним может не оказаться. */}
                    {showUnconfirmed(s?.crop_id) && (
                      <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className="shrink-0 text-muted-foreground"
                            data-testid="map-sow-unconfirmed"
                          >
                            · не подтверждена в справочнике
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          Культура не подтверждена в справочнике хозяйства — поэтому
                          на карте поле серое, а в легенде оно в «Не разобрано».
                        </TooltipContent>
                      </Tooltip>
                      </TooltipProvider>
                    )}
                  </span>
                ) : (
                  <span className="truncate text-muted-foreground">{subject.label}</span>
                )}
                {/* У ПАРА вместо этапа лестницы — состояние поля одним словом
                    («Парует», у АМП — «АМП»): подпись приходит с backend'а
                    (`crop_status_label`). Лестница сева тут бессмысленна — пар
                    не сеют и не убирают, а «Не запланирован» рядом с «Пар
                    чёрный» читался бы как дыра в плане, хотя пар — осознанное
                    решение агронома. */}
                {eff && subject.kind === 'fallow' && (
                  <span className="shrink-0 text-muted-foreground" data-testid="map-sow-fallow-status">
                    {eff.crop_status_label ?? 'Парует'}
                  </span>
                )}
                {eff && subject.kind === 'crop' && (
                  <span
                    className="shrink-0 text-muted-foreground"
                    title={unrealized?.hint ?? undefined}
                  >
                    {stageLabel}
                  </span>
                )}
              </div>
              {/* AK-509: частичная уборка/сев — цифра РЯДОМ со статусом
                  «Растёт», не отдельный статус (ADR-059 §4a.5). */}
              {eff && subject.kind === 'crop' && sowingStageQuantity(eff) && (
                <div className="text-xs text-muted-foreground" data-testid="map-sow-quantity">
                  {sowingStageQuantity(eff)}
                </div>
              )}
              {showSow && canWrite && (
                <>
                  <Button
                    size="sm"
                    className="h-7 w-full text-xs"
                    disabled={busy || !seasonGuard.canWrite}
                    aria-label={
                      zoned ? `Отметить посеяно: ${leaf.name}` : 'Отметить: посеяно'
                    }
                    onClick={() => void markSown(leaf)}
                  >
                    {busy && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                    {zoned ? 'Посеяно' : 'Отметить: посеяно'}
                  </Button>
                  {/* Причина блокировки — ВИДИМОЙ строкой, не title: у DS-кнопки
                      disabled:pointer-events-none (нативный тултип не всплывает),
                      а на планшете в поле hover'а нет вовсе (CLAUDE.md: hover-only
                      взаимодействия запрещены). */}
                  {!seasonGuard.canWrite && (
                    <div
                      className="text-xs leading-tight text-muted-foreground"
                      data-testid="map-sow-locked-reason"
                    >
                      {seasonGuard.reason}
                    </div>
                  )}
                </>
              )}
              {subject.kind === 'none' && (
                <a
                  href={planHref(fieldId)}
                  className="text-xs font-medium text-primary hover:underline"
                  data-testid="map-sow-plan-link"
                >
                  Запланировать →
                </a>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="map-sow-block">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {body}
    </div>
  );
}
